import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { TENANT_BANDS, type TenantBand } from '../lib/signupState';

// Four large tap targets backed by native radios (keyboard + screen reader
// behaviour for free).
const useStyles = makeStyles({
  group: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    ...shorthands.border(0),
    padding: 0,
    margin: 0,
    minWidth: 0,
  },
  legend: {
    padding: 0,
    marginBottom: '6px',
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  chip: { position: 'relative', display: 'block' },
  input: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    opacity: 0,
    cursor: 'pointer',
    ':focus-visible + span': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
    ':checked + span': {
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.borderColor(tokens.colorBrandBackground),
    },
  },
  face: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '44px',
    padding: '8px 4px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    pointerEvents: 'none',
  },
  error: { gridColumn: '1 / -1', margin: 0, color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
});

interface Props {
  value: TenantBand | '';
  onChange: (v: TenantBand) => void;
  error?: string;
}

export function BandPicker({ value, onChange, error }: Props) {
  const s = useStyles();
  return (
    <fieldset className={s.group} aria-describedby={error ? 'band-error' : undefined}>
      <legend className={s.legend}>How many client tenants do you manage?</legend>
      {TENANT_BANDS.map((band) => (
        <label key={band} className={s.chip}>
          <input
            className={s.input}
            type="radio"
            name="tenantBand"
            value={band}
            checked={value === band}
            onChange={() => onChange(band)}
          />
          <span className={s.face}>{band.replace('-', '–')}</span>
        </label>
      ))}
      {error && (
        <p id="band-error" role="alert" className={s.error}>
          {error}
        </p>
      )}
    </fieldset>
  );
}
